import { useMemo } from "react";
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { ITableProps } from "./types";
import cs from "classnames";
import { PredefinedColumns, Settings } from "./settings";
import { VscLoading } from "react-icons/vsc";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Pagination } from "@mantine/core";
import { BsSortUp, BsSortUpAlt } from "react-icons/bs";

const Table = <Data,>({
    columns,
    data,
    loading,
    footer = false,
    pagination = {
        isStartFromZero: true,
        queryKey: "page",
        lastPage: 0,
        activePage: 1,
    },
    options = {
        rowNumber: true,
    },
    sort = {
        sortableItems: [],
        queryKeys: {
            field: "",
            type: "",
        },
    },
    classNames = {
        tableClassName: "",
        tbodyClassName: "",
        theadClassName: "",
        tfootClassName: "",
    },
    Filters,
}: ITableProps<Data>) => {
    const { tableClassName, tbodyClassName, theadClassName, tfootClassName } =
        classNames;

    const [URLSearchParams] = useSearchParams();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    // Memorize data and columns for any changes
    const memorizeData = useMemo(() => data, [data]);
    const memorizeColumns = useMemo(() => columns, [columns]);

    const { getHeaderGroups, getRowModel, getFooterGroups } = useReactTable({
        data: memorizeData,
        columns: [
            ...(options.rowNumber
                ? [PredefinedColumns<Data>("ردیف").rowNumber]
                : []),
            ...memorizeColumns,
        ],
        getCoreRowModel: getCoreRowModel(),
    });

    const handleSort = (id: keyof Data) => {
        if (!sort || !sort.sortableItems.includes(id)) {
            return null;
        }
        const oldValue = URLSearchParams.get(sort.queryKeys.type);

        URLSearchParams.set(
            sort.queryKeys.type,
            oldValue === Settings.sortTypes.asc
                ? Settings.sortTypes.desc
                : oldValue === Settings.sortTypes.desc
                ? ""
                : Settings.sortTypes.asc,
        );

        URLSearchParams.set(
            sort.queryKeys.field,
            oldValue === Settings.sortTypes.desc ? "" : id.toString(),
        );

        URLSearchParams.set(pagination.queryKey, "1");

        navigate(
            {
                pathname,
                search: "?" + URLSearchParams.toString(),
            },
            {
                replace: true,
            },
        );
    };

    const handleChangePage = (page: number) => {
        URLSearchParams.set(pagination.queryKey, page.toString());

        navigate(
            {
                pathname,
                search: "?" + URLSearchParams.toString(),
            },
            {
                replace: true,
            },
        );
    };

    const handlePerPage = (value: string) => {
        URLSearchParams.set("per_page", value);

        navigate(
            {
                pathname,
                search: "?" + URLSearchParams.toString(),
            },
            {
                replace: true,
            },
        );
    };

    return (
        <section className="dashboard-table flex flex-col h-full overflow-hidden">
            {Filters && (
                <section className="mb-4">
                    <Filters data={data} />
                </section>
            )}

            <div className="max-h-full scroll-gray-400 overflow-auto">
                <section className="h-full shadow-sm rounded-md border border-solid border-gray-300 overflow-auto scroll-gray-400 scroll-gray-400-horizontal">
                    <table
                        className={cs(
                            "w-full overflow-auto border-spacing-0",
                            tableClassName,
                        )}>
                        {/* Render Header */}
                        <thead
                            className={cs(
                                "bg-gray-300 text-gray-700 sticky top-0 rounded-t-md z-10",
                                theadClassName,
                            )}>
                            {getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            className={cs(
                                                "py-3 px-3 text-start min-w-max max-w-xs align-middle flex-grow",
                                                header.column.id ===
                                                    "rowNumber" &&
                                                    "text-center",
                                                sort &&
                                                    sort.sortableItems.includes(
                                                        header.column
                                                            .id as keyof Data,
                                                    ) &&
                                                    "cursor-pointer",
                                            )}
                                            onClick={() =>
                                                handleSort(
                                                    header.column
                                                        .id as keyof Data,
                                                )
                                            }>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef
                                                          .header,
                                                      header.getContext(),
                                                  )}

                                            {URLSearchParams.get(
                                                sort?.queryKeys.field,
                                            ) === header.column.id &&
                                                URLSearchParams.get(
                                                    sort?.queryKeys.type,
                                                ) ===
                                                    Settings.sortTypes.asc && (
                                                    <BsSortUpAlt className="text-2xl align-middle mr-4" />
                                                )}
                                            {URLSearchParams.get(
                                                sort?.queryKeys.field,
                                            ) === header.column.id &&
                                                URLSearchParams.get(
                                                    sort?.queryKeys.type,
                                                ) ===
                                                    Settings.sortTypes.desc && (
                                                    <BsSortUp className="text-2xl align-middle mr-4" />
                                                )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>

                        {/* Render Body */}
                        <tbody className={cs(tbodyClassName)}>
                            {!loading && !!data.length && (
                                <>
                                    {getRowModel().rows.map((row, i) => (
                                        <tr key={row.id}>
                                            {row
                                                .getVisibleCells()
                                                .map((cell) => (
                                                    <td
                                                        key={cell.id}
                                                        className={cs(
                                                            "px-3 py-4 min-w-max max-w-xs align-middle break-words overflow-hidden flex-grow",
                                                            cell.column.id ===
                                                                "rowNumber" &&
                                                                "font-numeric text-center w-9",
                                                            i % 2 === 1
                                                                ? "bg-gray-50"
                                                                : "bg-white",
                                                        )}>
                                                        {flexRender(
                                                            cell.column
                                                                .columnDef.cell,
                                                            cell.getContext(),
                                                        )}
                                                    </td>
                                                ))}
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>

                        {/* Render Footer */}
                        {footer && (
                            <tfoot
                                className={cs(
                                    "bg-gray-600 text-white text-sm",
                                    tfootClassName,
                                )}>
                                {getFooterGroups().map((footerGroup) => (
                                    <tr key={footerGroup.id}>
                                        {footerGroup.headers.map((header) => (
                                            <th
                                                key={header.id}
                                                className={cs(
                                                    "py-4 px-3 text-start min-w-max max-w-xs align-middle",
                                                    header.column.id ===
                                                        "rowNumber" &&
                                                        "text-center",
                                                )}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                          header.column
                                                              .columnDef.footer,
                                                          header.getContext(),
                                                      )}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </tfoot>
                        )}
                    </table>

                    {!loading && !data.length && (
                        <div className="text-center py-12 font-medium text-xl bg-white">
                            موردی یافت نشد
                        </div>
                    )}

                    {loading && (
                        <div className="bg-white py-10 px-4 text-center">
                            <VscLoading className="text-6xl mx-auto text-primary animate-spin" />
                        </div>
                    )}
                </section>
            </div>

            <section className="flex items-center text-center mt-auto pt-4">
                {pagination && pagination.lastPage > 1 && (
                    <Pagination
                        color="blue"
                        className="mx-auto py-2"
                        classNames={{
                            item: "font-numeric shadow-md rounded-full text-base border-gray-200",
                        }}
                        page={pagination.activePage}
                        onChange={(page) => handleChangePage(page)}
                        total={pagination.lastPage}
                    />
                )}
            </section>
        </section>
    );
};

export default Table;
